import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { useGetAllCities } from 'src/api/account';
import { useGetCustomers } from 'src/api/customer';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import { useAuthContext } from 'src/auth/hooks';
import { components } from 'src/types/schema';
import UserTableToolbar from '../customer-table-toolbar';

// ----------------------------------------------------------------------

interface CustomerFiltersState {
  name: string;
  status: string;
}

const defaultFilters: CustomerFiltersState = {
  name: '',
  status: 'all',
};

const CUSTOMERS_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Unverified' },
];

const TABLE_HEAD = [
  { id: 'phoneNumber', label: 'Phone', width: 180 },
  { id: 'name', label: 'Name', width: 220 },
  { id: 'subscription', label: 'Subscriptions' },
  { id: 'comment', label: 'Comment' },
];

// ----------------------------------------------------------------------

export default function CustomerAdminListView() {
  const table = useTable({ defaultRowsPerPage: 25 });
  const [searchParams, setSearchParams] = useSearchParams();
  const router = useRouter();
  const { user } = useAuthContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<components["schemas"]["RestrictedPartnersCustomer"][][number]|undefined>();
  // Derive filters directly from searchParams - always use string values for consistency
  const filters: CustomerFiltersState = {
    name: searchParams.get('name') || '',
    status: searchParams.get('status') || 'all',
  };

  const page = Number(searchParams.get('page')) || 0;
  const rowsPerPage = Number(searchParams.get('rowsPerPage')) || table.rowsPerPage;

  // Convert string status to boolean for API call
  const statusForApi = useMemo(() => {
    if (filters.status === 'true') return true;
    if (filters.status === 'false') return false;
    return undefined; // 'all' case
  }, [filters.status]);

  // API call with pagination and filtering
  const { customers, customersLoading, customersError, customersEmpty } = useGetCustomers(
    page + 1, // API is 1-based index
    rowsPerPage,
    filters.name || undefined, // Search term for both phone_number and name
    statusForApi
  );

  const tableData = customers?.results || []; // Use API results directly

  const denseHeight = table.dense ? 52 : 72;

  const canReset = !isEqual(defaultFilters, filters);
  const notFound = !tableData.length && !customersLoading;

  // Handler for changing filters
  const handleFilters = useCallback(
    (name: string, value: string | boolean | null) => {
      const params = new URLSearchParams(searchParams);
      if (value !== undefined && value !== null && value !== '') {
        params.set(name, String(value));
      } else {
        params.delete(name);
      }
      params.set('page', '0'); // Reset page when filters change
      setSearchParams(params);
      table.onResetPage(); // Reset table page state
    },
    [searchParams, setSearchParams, table]
  );

  // Handler specifically for the status tab filter
  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  // Handler for changing page
  const handlePageChange = useCallback(
    (event: unknown, newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // Handler for changing rows per page
  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      const params = new URLSearchParams(searchParams);
      params.set('rowsPerPage', newRowsPerPage.toString());
      params.set('page', '0'); // Reset page when changing rows per page
      setSearchParams(params);
      table.onChangeRowsPerPage(event); // Update table state
    },
    [searchParams, setSearchParams, table]
  );

  // Handler to reset all filters
  const handleResetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams()); // Clear all search params
    table.onResetPage(); // Reset table page state
  }, [setSearchParams, table]);

  // Handler for opening customer details modal
  const handleOpenCustomerDetails = (customer: any) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  // Handler for closing customer details modal
  const handleCloseCustomerDetails = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleRedirectToSubscriptions = () => {
    const phoneNumber = selectedCustomer?.user?.phone_number;
    if (phoneNumber) {
      router.push(`${paths.dashboard.restaurant.subscription.subscriptions}?title=${encodeURIComponent(phoneNumber)}`);
    }
  };

  return (
    <PermissionBasedGuard permissions={['partners:read:customers']} hasContent>
      <Container maxWidth={false}>
        <CustomBreadcrumbs
          heading="Customers"
          links={[{}]}
          sx={{
            mb: 2,
          }}
        />
        
        <Card>
          <Tabs
            value={filters.status}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {CUSTOMERS_TYPE_OPTIONS.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
              />
            ))}
          </Tabs>

          <UserTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            roleOptions={[]}
          />

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={customers?.count || 0}
                  onSort={table.onSort}
                />

                <TableBody>
                  {customersLoading ? (
                    [...Array(rowsPerPage)].map((_, index) => (
                      <TableSkeleton key={index} sx={{ height: denseHeight }} />
                    ))
                  ) : (
                    tableData.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleOpenCustomerDetails(row)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.lighter, 0.08)
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.user?.phone_number || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.name || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.subscriptions_count ?? 0}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {/* @ts-ignore - just ignore this type error it will be fixed from the backend schema later */}
                            {row.comment || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, customers?.count || 0)}
                  />

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={customers?.count || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>

        {/* Customer Details Modal */}
        <Dialog
          fullWidth
          maxWidth="md"
          open={isModalOpen}
          onClose={handleCloseCustomerDetails}
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: 'calc(100% - 64px)'
            }
          }}
        >
          {selectedCustomer && (
            <>
              <DialogTitle>Customer Details</DialogTitle>

              <DialogContent dividers sx={{ p: 3 }}>
                <Box>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Customer Name"
                        disabled
                        value={selectedCustomer.name || '-'}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="User ID"
                        disabled
                        value={selectedCustomer.user?.id || '-'}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Phone Number"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="twemoji:flag-kuwait" sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                    value={selectedCustomer.user?.phone_number || 'N/A'}
                    sx={{ mb: 3 }}
                  />

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Area"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.area.name_ar || '-'}
                      />
                    </Grid>

                    <Grid item xs={3} md={2}>
                      <TextField
                        fullWidth
                        label="Avenue"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.avenue || '-'}
                      />
                    </Grid>

                    <Grid item xs={3} md={2}>
                      <TextField
                        fullWidth
                        label="Block"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.block || '-'}
                      />
                    </Grid>

                    <Grid item xs={3} md={2}>
                      <TextField
                        fullWidth
                        label="House"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.house || '-'}
                      />
                    </Grid>

                    <Grid item xs={3} md={2}>
                      <TextField
                        fullWidth
                        label="Street"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.street || '-'}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Subscriptions Count"
                        variant="outlined"
                        disabled
                        value={selectedCustomer.subscriptions_count || '0'}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Is Verified"
                        variant="outlined"
                        disabled
                        value={selectedCustomer?.is_verified ? 'Yes' : 'No'}
                      />
                    </Grid>
                  </Grid>

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Comment"
                      variant="outlined"
                      disabled
                      value={selectedCustomer.comment || '-'}
                      sx={{ mt: 3 }}
                    />
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCloseCustomerDetails}
                  startIcon={<Iconify icon="eva:close-fill" />}
                >
                  Close
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedCustomer?.subscriptions_count}
                  onClick={handleRedirectToSubscriptions}
                  startIcon={<Iconify icon="eva:diagonal-arrow-right-up-fill" />}
                >
                  View Subscriptions
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </PermissionBasedGuard>
  );
}
